import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../lib/api'

function formatTime(value) {
	if (!value) return ''
	const date = new Date(value)
	return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function MetaPill({ children, className = '' }) {
	return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${className}`}>{children}</span>
}

export default function ProfilePage({ currentUser, onLogin, userId, onEditProfile, onOpenProfile }) {
	const [profile, setProfile] = useState(null)
	const [stories, setStories] = useState([])
	const [posts, setPosts] = useState([])
	const [loading, setLoading] = useState(false)
	const [activePost, setActivePost] = useState(null)
	const [activeStoryIndex, setActiveStoryIndex] = useState(0)
	const [storyViewerOpen, setStoryViewerOpen] = useState(false)
	const [storyViewsModalOpen, setStoryViewsModalOpen] = useState(false)
	const [likesModalOpen, setLikesModalOpen] = useState(false)
	const [commentsModalOpen, setCommentsModalOpen] = useState(false)
	const [likesModalLoading, setLikesModalLoading] = useState(false)
	const [commentsModalLoading, setCommentsModalLoading] = useState(false)
	const [modalLikes, setModalLikes] = useState([])
	const [modalComments, setModalComments] = useState([])
	const [commentDraft, setCommentDraft] = useState('')
	const [commentSubmitting, setCommentSubmitting] = useState(false)
	const [profileImageOpen, setProfileImageOpen] = useState(false)

	const resolvedUserId = userId || currentUser?.id || null

	useEffect(() => {
		async function loadProfile() {
			if (!resolvedUserId && !currentUser) return
			setLoading(true)
			try {
				const path = resolvedUserId ? `/profile/${resolvedUserId}` : '/profile/me'
				const res = await apiFetch(path)
				if (!res.ok) throw new Error('Failed to load profile')
				const data = await res.json()
				setProfile(data.profile || null)
				setStories(Array.isArray(data.stories) ? data.stories : [])
				setPosts(Array.isArray(data.posts) ? data.posts : [])
			} catch {
				setProfile(null)
				setStories([])
				setPosts([])
			} finally {
				setLoading(false)
			}
		}

		loadProfile()
	}, [resolvedUserId, currentUser?.id])

	function updatePost(postId, updater) {
		setPosts((prev) => prev.map((post) => (post.id === postId ? updater(post) : post)))
		setActivePost((prev) => (prev && prev.id === postId ? updater(prev) : prev))
	}

	async function toggleLike(postId) {
		if (!currentUser) return onLogin?.()
		const existing = posts.find((post) => post.id === postId) || activePost
		if (!existing) return

		const previousLiked = !!existing.likedByMe
		const previousCount = Number(existing.likesCount || 0)
		const optimisticLiked = !previousLiked
		const optimisticCount = Math.max(0, previousCount + (optimisticLiked ? 1 : -1))

		updatePost(postId, (post) => ({ ...post, likedByMe: optimisticLiked, likesCount: optimisticCount }))

		try {
			const res = await apiFetch(`/posts/${postId}/like`, { method: 'POST' })
			if (!res.ok) throw new Error('Failed to toggle like')
			const data = await res.json()
			updatePost(postId, (post) => ({ ...post, likedByMe: data.liked, likesCount: data.likesCount }))
		} catch {
			updatePost(postId, (post) => ({ ...post, likedByMe: previousLiked, likesCount: previousCount }))
		}
	}

	async function openLikesModal(post) {
		setActivePost(post)
		setLikesModalOpen(true)
		setLikesModalLoading(true)
		setModalLikes([])
		try {
			const res = await apiFetch(`/posts/${post.id}/likes`)
			if (!res.ok) throw new Error('Failed to load likes')
			const data = await res.json()
			setModalLikes(Array.isArray(data.likes) ? data.likes : [])
		} catch {
			setModalLikes([])
		} finally {
			setLikesModalLoading(false)
		}
	}

	async function openCommentsModal(post) {
		setActivePost(post)
		setCommentsModalOpen(true)
		setCommentsModalLoading(true)
		setModalComments([])
		setCommentDraft('')
		try {
			const res = await apiFetch(`/posts/${post.id}/comments`)
			if (!res.ok) throw new Error('Failed to load comments')
			const data = await res.json()
			setModalComments(Array.isArray(data.comments) ? data.comments : [])
		} catch {
			setModalComments([])
		} finally {
			setCommentsModalLoading(false)
		}
	}

	async function submitComment() {
		if (!currentUser) return onLogin?.()
		if (!activePost) return
		const text = commentDraft.trim()
		if (!text) return

		setCommentSubmitting(true)
		try {
			const res = await apiFetch(`/posts/${activePost.id}/comments`, {
				method: 'POST',
				body: JSON.stringify({ text }),
			})
			if (!res.ok) return
			const comment = await res.json()
			setModalComments((prev) => [comment, ...prev])
			updatePost(activePost.id, (post) => ({
				...post,
				commentsCount: comment.commentsCount,
				comments: [comment, ...(post.comments || [])],
			}))
			setCommentDraft('')
		} finally {
			setCommentSubmitting(false)
		}
	}

	async function toggleFollowProfile() {
		if (!currentUser) return onLogin?.()
		if (!profile || profile.isMe) return

		const previous = !!profile.isFollowing
		const optimisticFollowers = Math.max(0, Number(profile.followersCount || 0) + (previous ? -1 : 1))
		setProfile((prev) => (prev ? { ...prev, isFollowing: !previous, followersCount: optimisticFollowers } : prev))

		try {
			const res = await apiFetch(`/profile/${profile.id}/follow`, { method: previous ? 'DELETE' : 'POST' })
			if (!res.ok) throw new Error('Failed to update follow')
			const data = await res.json()
			setProfile((prev) => (prev ? { ...prev, isFollowing: !!data.following, followersCount: data.followersCount } : prev))
			setPosts((prev) => prev.map((post) => ({ ...post, isFollowingAuthor: !!data.following })))
			setActivePost((prev) => (prev ? { ...prev, isFollowingAuthor: !!data.following } : prev))
		} catch {
			setProfile((prev) => (prev ? { ...prev, isFollowing: previous, followersCount: Number(profile.followersCount || 0) } : prev))
		}
	}

	if (!currentUser && !userId) {
		return (
			<div className="rounded-3xl border border-gray-200 bg-white p-5 text-sm text-gray-600">
				Please login to view your profile.
			</div>
		)
	}

	const profileLocation = profile?.location
	const profilePreferences = profile?.preferences
	const storyItems = useMemo(() => stories.slice(0, 20), [stories])
	const activeStory = storyItems[activeStoryIndex] || null

	function openStoryViewer(index) {
		setActiveStoryIndex(index)
		setStoryViewerOpen(true)
	}

	useEffect(() => {
		if (!storyViewerOpen || !activeStory?.id || !currentUser?.id) return

		apiFetch(`/home/stories/${activeStory.id}/view`, { method: 'POST' }).catch(() => {})
	}, [storyViewerOpen, activeStory?.id, currentUser?.id])

	function goToNextStory() {
		if (!storyItems.length) return
		setActiveStoryIndex((prev) => (prev + 1) % storyItems.length)
	}

	function goToPreviousStory() {
		if (!storyItems.length) return
		setActiveStoryIndex((prev) => (prev - 1 + storyItems.length) % storyItems.length)
	}

	return (
		<section className="mx-auto w-full max-w-4xl">
			{loading && <p className="text-sm font-semibold text-gray-600">Loading profile...</p>}

			{!loading && profile && (
				<>
					<div className="rounded-3xl border border-white/60 bg-white/90 p-4 shadow-lg">
						<div className="flex items-start gap-4">
								<button type="button" onClick={() => profile.profilePicture && setProfileImageOpen(true)} className="shrink-0">
									<img
										src={profile.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(profile.name)}`}
										alt={profile.name}
										className="h-20 w-20 rounded-2xl object-cover"
									/>
								</button>
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-center justify-between gap-2">
									<h2 className="truncate text-xl font-bold text-gray-900">{profile.name}</h2>
									<div className="flex gap-2">
										{!profile.isMe && (
											<button
												onClick={toggleFollowProfile}
												className={`rounded-full px-4 py-2 text-sm font-semibold ${profile.isFollowing ? 'border border-gray-300 text-gray-700' : 'bg-orange-500 text-white'}`}
											>
												{profile.isFollowing ? 'Following' : 'Follow'}
											</button>
										)}
										{profile.isMe && (
											<button onClick={onEditProfile} className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">
												Edit Profile
											</button>
										)}
									</div>
								</div>
								<p className="mt-1 text-sm text-gray-600">{profile.bio || 'No bio yet.'}</p>
								{!!(profileLocation?.area || profileLocation?.city) && (
									<p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-orange-700">
										{profileLocation?.area ? `${profileLocation.area}, ` : ''}{profileLocation?.city || ''}
									</p>
								)}
								<div className="mt-3 flex gap-4 text-xs font-semibold text-gray-700">
									<span>{profile.totalPosts || 0} posts</span>
									<span>{profile.followersCount || 0} followers</span>
									<span>{profile.followingCount || 0} following</span>
								</div>
							</div>
						</div>

						<div className="mt-4">
							<p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Preferences</p>
							<div className="mt-2 flex flex-wrap gap-2">
								{profilePreferences?.dietaryType && (
									<MetaPill className="bg-orange-100 text-orange-700">{profilePreferences.dietaryType}</MetaPill>
								)}
								{profilePreferences?.budgetVibe && (
									<MetaPill className="bg-amber-100 text-amber-700">{profilePreferences.budgetVibe}</MetaPill>
								)}
								{profile.availability?.label && (
									<MetaPill className="bg-green-100 text-green-700">{profile.availability.label}</MetaPill>
								)}
								{!profilePreferences?.dietaryType && !profilePreferences?.budgetVibe && !profile.availability?.label && (
									<span className="text-xs text-gray-400">No preferences added yet.</span>
								)}
							</div>
						</div>

						<div className="mt-4">
							<p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Interests</p>
							<div className="mt-2 flex flex-wrap gap-2">
								{Array.isArray(profilePreferences?.interests) && profilePreferences.interests.length > 0 ? (
									profilePreferences.interests.map((interest) => (
										<MetaPill key={interest} className="bg-gray-100 text-gray-700">{interest}</MetaPill>
									))
								) : (
									<span className="text-xs text-gray-400">No interests added yet.</span>
								)}
							</div>
						</div>
					</div>

						<div className="mt-4">
							<div className="mb-2 flex items-center justify-between">
								<p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Stories</p>
								<span className="text-[11px] text-gray-400">History</span>
							</div>
							{storyItems.length > 0 ? (
								<div className="flex gap-3 overflow-x-auto pb-1">
									{storyItems.map((story, index) => (
										<button key={story.id} type="button" onClick={() => openStoryViewer(index)} className="min-w-[72px] text-center">
											<div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-orange-300 to-red-400 p-[2px]">
												<img
													src={story.mediaUrl || story.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(story.user.name)}`}
													alt={story.user.name}
													className="h-full w-full rounded-full bg-white object-cover"
												/>
											</div>
											<p className="mt-1 truncate text-[11px] font-semibold text-gray-700">{story.user.name}</p>
										</button>
									))}
								</div>
							) : (
								<p className="text-xs text-gray-400">No stories yet.</p>
							)}
						</div>

					<div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
						{posts.map((post) => (
							<button
								key={post.id}
								onClick={() => setActivePost(post)}
								className="group aspect-square overflow-hidden rounded-2xl border border-gray-200 bg-gray-100"
							>
								{post.mediaUrl ? (
									<img src={post.mediaUrl} alt="post" className="h-full w-full object-cover transition group-hover:scale-105" />
								) : (
									<div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-orange-100 to-amber-200 p-2 text-center text-xs font-semibold text-gray-700">
										{post.content || 'Post'}
									</div>
								)}
							</button>
						))}
					</div>
				</>
			)}

			{storyViewerOpen && activeStory && (
				<div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/75 px-3">
					<div className="w-full max-w-md overflow-hidden rounded-3xl border border-white/20 bg-gray-950 text-white shadow-2xl">
						<div className="flex items-center gap-3 border-b border-white/15 p-3">
							<img
								src={activeStory.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(activeStory.user.name)}`}
								alt={activeStory.user.name}
								className="h-9 w-9 rounded-full object-cover"
							/>
							<div className="min-w-0 flex-1">
								<p className="truncate text-sm font-semibold">{activeStory.user.name}</p>
								<p className="text-xs text-gray-300">{formatTime(activeStory.createdAt)}</p>
							</div>
							<button type="button" onClick={() => setStoryViewerOpen(false)} className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">Close</button>
						</div>
						<div className="relative">
							{activeStory.mediaUrl ? (
								<img src={activeStory.mediaUrl} alt="Story" className="h-[60vh] w-full object-cover" />
							) : (
								<div className="flex h-[60vh] w-full items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 p-6 text-center text-lg font-semibold">
									{activeStory.caption || 'Story'}
								</div>
							)}
							<button
								type="button"
								onClick={goToPreviousStory}
								className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 px-2 py-1 text-lg"
							>
								‹
							</button>
							<button
								type="button"
								onClick={goToNextStory}
								className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 px-2 py-1 text-lg"
							>
								›
							</button>
						</div>
						{activeStory.caption && (
							<div className="border-t border-white/15 p-3 text-sm text-gray-100">{activeStory.caption}</div>
						)}
						{profile?.isMe && typeof activeStory.viewersCount === 'number' && (
							<div className="border-t border-white/15 px-3 py-2">
								<button
									type="button"
									onClick={() => setStoryViewsModalOpen(true)}
									className="text-sm font-semibold text-orange-200 hover:text-white"
								>
									Seen by {activeStory.viewersCount}
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			{storyViewsModalOpen && activeStory && (
				<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-3">
					<div className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<h3 className="text-base font-bold text-gray-900">Seen by</h3>
							<button type="button" onClick={() => setStoryViewsModalOpen(false)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>
						<div className="max-h-[60vh] overflow-y-auto p-3">
							{!activeStory.viewers?.length && <p className="text-sm text-gray-500">No views yet.</p>}
							{activeStory.viewers?.map((entry) => (
								<div key={entry.id} className="flex items-center gap-3 rounded-xl px-2 py-2">
									<button type="button" onClick={() => onOpenProfile?.(entry.user?.id)} className="shrink-0">
										<img
											src={entry.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(entry.user.name)}`}
											alt={entry.user.name}
											className="h-10 w-10 rounded-full object-cover"
										/>
									</button>
									<div className="min-w-0 flex-1">
										<button type="button" onClick={() => onOpenProfile?.(entry.user?.id)} className="truncate text-sm font-semibold text-gray-900">{entry.user.name}</button>
										<p className="text-xs text-gray-500">{formatTime(entry.createdAt)}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{activePost && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-3">
					<div className="w-full max-w-xl overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<div className="flex min-w-0 items-center gap-2">
								<img
									src={activePost.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(activePost.user.name)}`}
									alt={activePost.user.name}
									className="h-9 w-9 rounded-full object-cover"
								/>
								<button onClick={() => onOpenProfile?.(activePost.user?.id)} className="truncate text-sm font-semibold text-gray-900">
									{activePost.user.name}
								</button>
							</div>
							<button onClick={() => setActivePost(null)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>

						<div className="max-h-[70vh] overflow-y-auto p-4">
							{activePost.content && <p className="whitespace-pre-wrap text-sm text-gray-800">{activePost.content}</p>}
							{activePost.mediaUrl && <img src={activePost.mediaUrl} alt="post" className="mt-3 w-full rounded-2xl border border-gray-200 object-cover" />}

							<div className="mt-3 flex items-center gap-4 text-sm">
								<button
									onClick={() => toggleLike(activePost.id)}
									aria-label="Like post"
									className={`rounded-full border p-2 font-semibold ${activePost.likedByMe ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-300 text-gray-700'}`}
								>
									{activePost.likedByMe ? '♥' : '♡'}
								</button>
								<button onClick={() => openLikesModal(activePost)} className="font-semibold text-gray-700">{activePost.likesCount || 0}</button>

								<button onClick={() => openCommentsModal(activePost)} aria-label="Open comments" className="rounded-full border border-gray-300 p-2 text-gray-700">💬</button>
								<button onClick={() => openCommentsModal(activePost)} className="font-semibold text-gray-700">{activePost.commentsCount || 0}</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{likesModalOpen && (
				<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-3">
					<div className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<h3 className="text-base font-bold text-gray-900">Liked by</h3>
							<button type="button" onClick={() => setLikesModalOpen(false)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>
						<div className="max-h-[60vh] overflow-y-auto p-3">
							{likesModalLoading && <p className="text-sm text-gray-500">Loading likes...</p>}
							{!likesModalLoading && modalLikes.length === 0 && <p className="text-sm text-gray-500">No likes yet.</p>}
							{modalLikes.map((entry) => (
								<div key={entry.id} className="flex items-center gap-3 rounded-xl px-2 py-2">
									<img
										src={entry.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(entry.user.name)}`}
										alt={entry.user.name}
										className="h-10 w-10 rounded-full object-cover"
									/>
									<div className="min-w-0 flex-1">
										<button onClick={() => onOpenProfile?.(entry.user?.id)} className="truncate text-sm font-semibold text-gray-900">{entry.user.name}</button>
										<p className="text-xs text-gray-500">{formatTime(entry.createdAt)}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{commentsModalOpen && (
				<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-3">
					<div className="flex h-[75vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<h3 className="text-base font-bold text-gray-900">Comments</h3>
							<button type="button" onClick={() => setCommentsModalOpen(false)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>
						<div className="flex-1 overflow-y-auto p-3">
							{commentsModalLoading && <p className="text-sm text-gray-500">Loading comments...</p>}
							{!commentsModalLoading && modalComments.length === 0 && <p className="text-sm text-gray-500">No comments yet.</p>}
							{modalComments.map((comment) => (
								<div key={comment.id} className="mb-2 rounded-xl bg-gray-50 px-3 py-2">
									<button onClick={() => onOpenProfile?.(comment.user?.id)} className="text-xs font-semibold text-gray-800">{comment.user.name}</button>
									<p className="text-sm text-gray-700">{comment.text}</p>
									<p className="mt-1 text-[11px] text-gray-500">{formatTime(comment.createdAt)}</p>
								</div>
							))}
						</div>
						<div className="border-t border-gray-200 p-3">
							<div className="flex gap-2">
								<input
									value={commentDraft}
									onChange={(e) => setCommentDraft(e.target.value)}
									className="flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm"
									placeholder="Write a comment..."
								/>
								<button
									onClick={submitComment}
									disabled={commentSubmitting}
									className="rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
								>
									{commentSubmitting ? 'Sending...' : 'Comment'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{profileImageOpen && profile?.profilePicture && (
				<div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 px-3">
					<div className="w-full max-w-2xl overflow-hidden rounded-3xl border border-white/20 bg-black shadow-2xl">
						<div className="flex items-center justify-between border-b border-white/15 px-4 py-3">
							<h3 className="text-sm font-bold text-white">Profile Photo</h3>
							<button type="button" onClick={() => setProfileImageOpen(false)} className="text-sm font-semibold text-gray-200">Close</button>
						</div>
						<img src={profile.profilePicture} alt={profile.name} className="max-h-[80vh] w-full bg-black object-contain" />
					</div>
				</div>
			)}
		</section>
	)
}
